import React, { useState } from "react";
import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Users, QrCode, Package, Gift, MessageSquare,
  Repeat, Share2, Crown, Handshake, TrendingUp, PieChart,
  Sparkles, BarChart3, ShoppingBag, Settings, CreditCard, LogOut, Menu, X, ChevronDown
} from "lucide-react";
import Logo from "@/components/Logo";
import { useAuth } from "@/lib/auth-context";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";

const navItems = [
  { section: "Ringkasan", items: [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, exact: true, roles: ["owner", "marketing", "cs", "finance"] },
  ]},
  { section: "Akuisisi & Pelanggan", items: [
    { to: "/dashboard/qr-loyalty", label: "QR Loyalty", icon: QrCode, roles: ["owner", "marketing"] },
    { to: "/dashboard/customers", label: "Customer Database", icon: Users, roles: ["owner", "marketing", "cs"] },
    { to: "/dashboard/orders", label: "Orders", icon: ShoppingBag, roles: ["owner", "cs", "finance"] },
    { to: "/dashboard/products", label: "Produk", icon: Package, roles: ["owner", "finance"] },
  ]},
  { section: "Retensi & Growth", items: [
    { to: "/dashboard/campaigns", label: "WhatsApp Campaign", icon: MessageSquare, roles: ["owner", "marketing"] },
    { to: "/dashboard/wa-integration", label: "WA Integration", icon: MessageSquare, roles: ["owner"] },
    { to: "/dashboard/repeat-order", label: "Repeat Order Engine", icon: Repeat, roles: ["owner", "marketing"] },
    { to: "/dashboard/rewards", label: "Cashback & Reward", icon: Gift, roles: ["owner", "marketing", "cs"] },
    { to: "/dashboard/referral", label: "Referral", icon: Share2, roles: ["owner", "marketing"] },
    { to: "/dashboard/membership", label: "Membership", icon: Crown, roles: ["owner", "marketing"] },
    { to: "/dashboard/reseller", label: "Reseller", icon: Handshake, roles: ["owner", "finance"] },
  ]},
  { section: "Insight", items: [
    { to: "/dashboard/profit", label: "Profit Dashboard", icon: PieChart, roles: ["owner", "finance"] },
    { to: "/dashboard/sku", label: "SKU Analytics", icon: TrendingUp, roles: ["owner", "finance"] },
    { to: "/dashboard/ai", label: "AI Assistant", icon: Sparkles, badge: "AI", roles: ["owner", "marketing"] },
    { to: "/dashboard/segmentation", label: "AI Segmentation", icon: Sparkles, badge: "AI", roles: ["owner", "marketing"] },
    { to: "/dashboard/benchmark", label: "Benchmark", icon: BarChart3, roles: ["owner", "finance"] },
  ]},
  { section: "Akun", items: [
    { to: "/dashboard/team", label: "Team Access", icon: Users, roles: ["owner"] },
    { to: "/dashboard/settings", label: "Pengaturan", icon: Settings, roles: ["owner"] },
    { to: "/dashboard/billing", label: "Billing", icon: CreditCard, roles: ["owner", "finance"] },
  ]},
];

const NavLink = ({ item }) => {
  const { pathname } = useLocation();
  const active = item.exact ? pathname === item.to : pathname === item.to || (item.to !== "/dashboard" && pathname.startsWith(item.to));
  const Icon = item.icon;
  return (
    <Link
      to={item.to}
      data-testid={`nav-${item.to.replace(/\//g, "-").slice(1)}`}
      className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
        active
          ? "bg-emerald-50 text-emerald-700"
          : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
      }`}
    >
      <Icon size={17} strokeWidth={active ? 2.2 : 1.8} />
      <span className="flex-1">{item.label}</span>
      {item.badge && (
        <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-amber-500 text-white">{item.badge}</span>
      )}
    </Link>
  );
};

export default function DashboardLayout() {
  const { merchant, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const initials = (merchant?.business_name || "OC").slice(0, 2).toUpperCase();

  // Determine current role for RBAC menu filtering
  const staffJson = typeof window !== "undefined" ? localStorage.getItem("oc_staff") : null;
  const staff = staffJson ? JSON.parse(staffJson) : null;
  const currentRole = staff?.role || "owner";

  const filterByRole = (items) => items.filter((i) => !i.roles || i.roles.includes(currentRole));

  const handleLogout = () => {
    logout();
    localStorage.removeItem("oc_staff");
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Sidebar - desktop */}
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-64 flex-col bg-white border-r border-gray-200 z-30">
        <div className="h-16 px-5 flex items-center border-b border-gray-200">
          <Logo size="sm" />
        </div>
        <nav className="flex-1 overflow-y-auto py-4 px-3 scrollbar-thin">
          {navItems.map((sec) => {
            const items = filterByRole(sec.items);
            if (items.length === 0) return null;
            return (
              <div key={sec.section} className="mb-5">
                <div className="px-3 mb-1.5 text-[10px] font-bold tracking-widest uppercase text-gray-400">{sec.section}</div>
                <div className="space-y-0.5">
                  {items.map((item) => <NavLink key={item.to} item={item} />)}
                </div>
              </div>
            );
          })}
        </nav>
        <div className="p-3 border-t border-gray-200">
          <div className="flex items-center gap-2 px-2 py-2">
            <Avatar className="h-8 w-8 bg-emerald-100">
              <AvatarFallback className="bg-emerald-100 text-emerald-700 text-xs font-bold">{initials}</AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-gray-900 truncate">{merchant?.business_name}</div>
              <div className="text-xs text-gray-500 truncate">{staff ? `${staff.name} · ${staff.role}` : merchant?.email}</div>
            </div>
          </div>
        </div>
      </aside>

      {/* Mobile sidebar overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-72 bg-white flex flex-col">
            <div className="h-16 px-5 flex items-center justify-between border-b border-gray-200">
              <Logo size="sm" />
              <button onClick={() => setMobileOpen(false)} data-testid="close-mobile-nav"><X /></button>
            </div>
            <nav className="flex-1 overflow-y-auto py-4 px-3 scrollbar-thin">
              {navItems.map((sec) => {
                const items = filterByRole(sec.items);
                if (items.length === 0) return null;
                return (
                  <div key={sec.section} className="mb-5">
                    <div className="px-3 mb-1.5 text-[10px] font-bold tracking-widest uppercase text-gray-400">{sec.section}</div>
                    <div className="space-y-0.5" onClick={() => setMobileOpen(false)}>
                      {items.map((item) => <NavLink key={item.to} item={item} />)}
                    </div>
                  </div>
                );
              })}
            </nav>
          </aside>
        </div>
      )}

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Topbar */}
        <header className="sticky top-0 z-20 h-16 bg-white border-b border-gray-200 flex items-center px-4 lg:px-8">
          <button className="lg:hidden mr-3" onClick={() => setMobileOpen(true)} data-testid="open-mobile-nav">
            <Menu />
          </button>
          <div className="flex-1">
            <div className="text-xs text-gray-500">Selamat datang kembali,</div>
            <div className="text-sm font-semibold text-gray-900 truncate">{merchant?.business_name}</div>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="hidden sm:inline-flex border-emerald-200 bg-emerald-50 text-emerald-700 capitalize">
              {staff ? `${staff.role}` : (merchant?.subscription_plan || "starter")}
            </Badge>
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg hover:bg-gray-100 transition-colors" data-testid="user-menu-trigger">
                <Avatar className="h-8 w-8">
                  <AvatarFallback className="bg-emerald-600 text-white text-xs font-bold">{initials}</AvatarFallback>
                </Avatar>
                <ChevronDown size={14} className="text-gray-500" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="font-semibold">{merchant?.owner_name}</div>
                  <div className="text-xs text-gray-500 font-normal">{merchant?.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/dashboard/settings")} data-testid="menu-settings">
                  <Settings className="mr-2 h-4 w-4" /> Pengaturan
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/dashboard/billing")} data-testid="menu-billing">
                  <CreditCard className="mr-2 h-4 w-4" /> Billing
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="menu-logout">
                  <LogOut className="mr-2 h-4 w-4" /> Keluar
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
